import { useTranslation } from 'react-i18next';

import { ErrorNotice } from '../../components/ErrorNotice';
import { InlineSelect } from '../../components/InlineSelect';
import { useMasterData } from '../masterdata/queries';
import { useAssignableUsers } from '../users/queries';
import type { Ticket } from './api';
import { useUpdateTicket } from './queries';

interface TicketQuickEditProps {
  ticket: Ticket;
  /** Ohne ``ticket.update`` stehen die Werte nur da. */
  canWrite: boolean;
}

/**
 * Prioritaet, Bearbeiter und Warteschlange direkt am Ticketkopf -- F1-42.
 *
 * Diese drei aendert ein Bearbeiter dutzendfach am Tag. Der Weg dorthin waren
 * vier Handgriffe: Reiter "Felder" oeffnen, Feld suchen, auswaehlen,
 * speichern. Hier ist es einer.
 *
 * **Der Status ist bewusst nicht dabei.** Ein Ticket wird nicht ueber eine
 * Feldaenderung geschlossen, sondern ueber eine benannte Aktion (docs/05,
 * Abschnitt 2) -- an einem Uebergang haengen Vorbedingungen, Protokoll und
 * die SLA-Uhr, und ein Auswahlfeld liesse all das aus. Die Zustandsknoepfe
 * stehen unveraendert daneben; sie sind der Weg, und sie sind kein Umweg.
 *
 * **`If-Match` traegt die Version mit.** Hat inzwischen jemand anders
 * gespeichert, antwortet der Server mit 409 statt den fremden Stand
 * stillschweigend zu ueberschreiben. Das gilt hier genauso wie im Formular:
 * Ein schneller Weg darf keine schwaechere Zusage haben als ein langsamer.
 */
export function TicketQuickEdit({ ticket, canWrite }: TicketQuickEditProps) {
  const { t } = useTranslation();
  const update = useUpdateTicket(ticket.id);

  const priorities = useMasterData('priority');
  const queues = useMasterData('queue');
  const users = useAssignableUsers();

  const change = (field: 'priority_id' | 'queue_id' | 'owner_id') => (value: string) => {
    update.mutate({
      // Leerer Wert heisst "niemand" und nicht "unveraendert" -- deshalb
      // ``null`` und nicht das leere Feld weglassen.
      payload: { [field]: value === '' ? null : value },
      version: ticket.version,
    });
  };

  // Welches der drei Felder gerade unterwegs ist. Ohne diese Unterscheidung
  // wuerden alle drei blass, sobald eines gespeichert wird -- und es saehe
  // aus, als haenge die ganze Zeile.
  const busy = (field: string) => update.isPending && field in update.variables.payload;

  return (
    <>
      <div className="quick-edit" role="group" aria-label={t('tickets.quickEdit')}>
        <span className="quick-edit__pair">
          <span className="quick-edit__label">{t('tickets.priority')}</span>
          <InlineSelect
            ariaLabel={t('tickets.priority')}
            value={ticket.priority.id}
            disabled={!canWrite}
            busy={busy('priority_id')}
            onChange={change('priority_id')}
            options={(priorities.data ?? []).map((entry) => ({
              value: entry.id,
              label: entry.name,
            }))}
          />
        </span>

        <span className="quick-edit__pair">
          <span className="quick-edit__label">{t('tickets.owner')}</span>
          <InlineSelect
            ariaLabel={t('tickets.owner')}
            value={ticket.owner_id ?? ''}
            disabled={!canWrite}
            busy={busy('owner_id')}
            onChange={change('owner_id')}
            options={[
              { value: '', label: t('tickets.unassigned') },
              // Abwesende bleiben in der Liste, tragen aber einen Hinweis:
              // Der Server leitet die Zuweisung auf die Vertretung um (K-70)
              // und vermerkt das am Ticket. Eine Zuweisung an einen
              // Abwesenden ist also zulaessig und hat eine Folge.
              ...(users.data ?? []).map((user) => ({
                value: user.id,
                label: user.out_of_office_until
                  ? `${user.full_name} (${t('tickets.outOfOffice')})`
                  : user.full_name,
              })),
            ]}
          />
        </span>

        <span className="quick-edit__pair">
          <span className="quick-edit__label">{t('tickets.queue')}</span>
          <InlineSelect
            ariaLabel={t('tickets.queue')}
            value={ticket.queue.id}
            disabled={!canWrite}
            busy={busy('queue_id')}
            onChange={change('queue_id')}
            options={(queues.data ?? []).map((entry) => ({ value: entry.id, label: entry.name }))}
          />
        </span>
      </div>

      {/* Der Fehler steht unter der Zeile und nicht in einer Meldung, die
          wieder verschwindet: Ein 409 heisst, dass der eigene Stand veraltet
          ist -- das muss man lesen koennen, waehrend man die Seite neu
          laedt. */}
      {update.isError ? <ErrorNotice error={update.error} /> : null}
    </>
  );
}
